import { useState, useEffect } from "react";
import { useAuth } from "@core/contexts/AuthContext";
import { pricingService, Price } from "@core/services/pricing-service";
import { useGeoLocation } from "@core/hooks/useGeoLocation";
import { FEATURES } from "@core/lib/features";
import { logError, ErrorFactory } from "@core/lib/error-handling";
import { PlanId } from "@core/constants/quotas";

export interface UseCheckoutOptions {
  returnPath: string; // e.g., "/date/dashboard"
}

export function useCheckout(options: UseCheckoutOptions) {
  const checkoutEnabled = FEATURES.CHECKOUT_ENABLED;
  const { user } = useAuth();
  const { geo } = useGeoLocation();
  const [prices, setPrices] = useState<Price[]>([]);
  const [loading, setLoading] = useState(false);
  const [needsAuth, setNeedsAuth] = useState(false);

  useEffect(() => {
    const fetchPrices = async () => {
      const countryCode = geo?.country || "FR";
      const regionId = await pricingService.getRegionForCountry(countryCode);
      const regionPrices = await pricingService.getPricingForRegion(regionId);
      setPrices(regionPrices);
    };
    fetchPrices();
  }, [geo]);

  const handleUpgrade = async (plan: PlanId) => {
    if (!checkoutEnabled) {
      alert("La fonction de paiement est temporairement désactivée.");
      return;
    }

    if (!user || !user.email) {
      localStorage.setItem("doodates-return-to", options.returnPath);
      setNeedsAuth(true);
      return;
    }

    // Nouveau modèle : Solo uniquement, cycle annual unique
    if (plan !== "SOLO") {
      logError(ErrorFactory.validation("Seul le plan Solo est disponible"), {
        component: "useCheckout",
      });
      return;
    }

    // Product ID pour Solo annual
    const productId = "solo_annual";

    const price = prices.find((p) => p.product_id === productId);

    if (!price?.lemonsqueezy_variant_id) {
      console.warn(
        `Price for ${productId} not found among:`,
        prices.map((p) => p.product_id),
      );
      alert("Plan non disponible pour le moment.");
      return;
    }

    setLoading(true);
    try {
      const url = await pricingService.createCheckoutSession(
        price.lemonsqueezy_variant_id,
        user.email,
        options.returnPath,
      );
      if (url) window.location.href = url;
    } catch (err) {
      logError(
        ErrorFactory.api("Checkout session creation failed", "Erreur lors du paiement.", {
          error: err,
        }),
        { component: "useCheckout", operation: "handleUpgrade" },
      );
      alert("Erreur lors du paiement.");
    } finally {
      setLoading(false);
    }
  };

  const dismissAuth = () => setNeedsAuth(false);

  return {
    prices,
    loading,
    needsAuth,
    handleUpgrade,
    dismissAuth,
    user,
    checkoutEnabled,
  };
}
